/**
 * RestoID — enterprise SSO email form.
 * ---------------------------------------------------------------------------
 * Drop-in replacement for the upstream `@/components/SingleSignOnForm`, which
 * renders a `SmartInputField` (44px control, floating label notched into a
 * fieldset, country prefix, clear button). The theme overlay can repaint that,
 * not restructure it, so the one screen that asks for an email looked nothing
 * like the rest of RestoID. Only the presentation is ours — every decision is
 * still the upstream hook's:
 *   - connector lookup + routing .. `useOnSubmit` (`use-check-single-sign-on`)
 *   - terms gate .................. `useTerms` + upstream `TermsAndPrivacyCheckbox`
 *   - remembered email ............ `UserInteractionContext.ssoEmail`
 *   - validation .................. react-hook-form + `validateIdentifierField`
 *                                   / `getGeneralIdentifierErrorMessage`
 *
 * Used by BOTH SSO entry screens: `custom/pages/SingleSignOnEmail` (the step
 * reached from "all sign-in options") and `custom/pages/SingleSignOnLanding`
 * (the SSO-first landing, which also shows the terms checkbox).
 *
 * The upstream CTA carries a lock glyph. RestoID's CTA is a text-only lime
 * button everywhere else (get code / sign in / continue), so the icon is
 * dropped rather than made the single exception — the screen head already says
 * this is the enterprise route.
 */
import { AgreeToTermsPolicy, SignInIdentifier } from '@logto/schemas';
import { useCallback, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import useOnSubmit from '@/hooks/use-check-single-sign-on';
import useTerms from '@/hooks/use-terms';
import { getGeneralIdentifierErrorMessage, validateIdentifierField } from '@/utils/form';

import { Button, Field, Stack } from './restoid';

type FormState = {
  email: string;
};

type Props = {
  isTermsAndPrivacyCheckboxVisible?: boolean;
};

const SsoEmailForm = ({ isTermsAndPrivacyCheckboxVisible }: Props) => {
  const { t } = useTranslation();
  const { errorMessage, clearErrorMessage, onSubmit } = useOnSubmit();
  const { ssoEmail } = useContext(UserInteractionContext);
  const { termsValidation, agreeToTermsPolicy } = useTerms();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isSubmitting },
  } = useForm<FormState>({
    reValidateMode: 'onBlur',
    // The email the user already typed once this session (upstream remembers it
    // on the context so coming back from the connector list refills the field).
    defaultValues: { email: ssoEmail ?? '' },
  });

  useEffect(() => {
    if (!isValid) {
      clearErrorMessage();
    }
  }, [clearErrorMessage, isValid]);

  const onSubmitHandler = useCallback(
    async (event?: React.FormEvent<HTMLFormElement>) => {
      /**
       * Prevent the default form submission behavior to avoid page reload.
       */
      event?.preventDefault();

      /**
       * Check if the user has agreed to the terms and privacy policy when the policy is set to `Manual`.
       */
      if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
        return;
      }

      clearErrorMessage();

      await handleSubmit(async ({ email }) => onSubmit(email, true))(event);
    },
    [agreeToTermsPolicy, clearErrorMessage, handleSubmit, onSubmit, termsValidation]
  );

  // The field's own validation message wins over the lookup error ("no SSO for
  // this email"), which upstream showed in a separate <ErrorMessage> directly
  // below the input — the same slot RestoID's Field renders its error in.
  const shownError = errors.email?.message ?? errorMessage;

  return (
    <form onSubmit={onSubmitHandler}>
      <Stack gap={18}>
        <Controller
          control={control}
          name="email"
          rules={{
            validate: (value) => {
              if (!value) {
                return getGeneralIdentifierErrorMessage([SignInIdentifier.Email], 'required');
              }

              const invalid = validateIdentifierField(SignInIdentifier.Email, value);

              return invalid
                ? getGeneralIdentifierErrorMessage([SignInIdentifier.Email], 'invalid')
                : true;
            },
          }}
          render={({ field: { value, onChange, onBlur, ref } }) => (
            <Field
              autoFocus
              // Same html contract the upstream SmartInputField gives a
              // single-type email field: type/inputMode/autocomplete + the
              // `input.email` phrase as its label.
              label={t('input.email')}
              inputType="email"
              inputMode="email"
              autoComplete="email"
              value={value}
              inputRef={ref}
              danger={Boolean(shownError)}
              errorMessage={shownError}
              // Upstream's SmartInputField trimmed on every change
              // (`use-smart-input-field` -> `setInputValue(value.trim())`), and
              // `emailRegEx` is /^\S+@\S+\.\S+$/ — so without this a pasted
              // " a@corp.com " would be rejected as invalid where upstream
              // accepted it. Trim here, not at submit, to match upstream exactly.
              onChange={(next) => {
                onChange(next.trim());
              }}
              onBlur={onBlur}
            />
          )}
        />

        {isTermsAndPrivacyCheckboxVisible ? <TermsAndPrivacyCheckbox /> : null}

        <Button htmlType="submit" title={t('action.single_sign_on')} isLoading={isSubmitting} />
      </Stack>
    </form>
  );
};

export default SsoEmailForm;
